/**
 * Cross-MFE navigation helpers.
 *
 * Shell and each micro-frontend mount their own `BrowserRouter`. Each Router
 * subscribes to its own `history` instance, and `pushState` only notifies
 * the Router that triggered the push. A push from inside an MFE therefore
 * updates `window.location` but leaves shell-side `useSearchParams` /
 * `useLocation` stale (and vice versa).
 *
 * Dispatching a synthetic `popstate` after `pushState` forces every Router
 * on the page to re-read the URL — the same workaround `single-spa`'s own
 * `navigateToUrl` uses. Using this helper in MFE code keeps that contract
 * in one place so individual call sites can't forget the dispatch.
 */
/**
 * Push a new URL and notify every Router on the page.
 *
 * Use this whenever an MFE needs to drive shell-hosted UI state that lives
 * in the URL (e.g. opening the shell EventComposer via the `event` query
 * param). For navigation that's purely internal to the calling app, prefer
 * the app's own router primitives — synthetic popstate dispatches are
 * cheap but not free.
 *
 * @example
 * pushSharedUrl(buildEventUrl(location.pathname + location.search, {
 *   mode: 'create',
 * }, seed));
 */
export function pushSharedUrl(url) {
    if (typeof window === 'undefined')
        return;
    window.history.pushState(null, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
}
/**
 * Same as `pushSharedUrl` but uses `replaceState` so the navigation does
 * not add a history entry. Use this for transient state changes (e.g.
 * closing a panel) where back-button traversal would feel wrong.
 */
export function replaceSharedUrl(url) {
    if (typeof window === 'undefined')
        return;
    window.history.replaceState(null, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
}
